import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Info } from 'lucide-react';

/**
 * MathExplainer — clickable info icon that opens a dialog showing formula breakdowns.
 *
 * Props:
 *  - title (string): Dialog heading
 *  - children (ReactNode): Full explanation content rendered inside the dialog
 *  - className (string): Extra classes on the trigger button
 *  - iconSize (number): Lucide icon size, default 3.5 (w-3.5 h-3.5)
 *  - variant: 'light' (white icon for dark bg) | 'dark' (slate icon for light bg)
 *  - inline (bool): If true renders as inline span instead of block button
 *  - testId (string): data-testid for the trigger
 */
export default function MathExplainer({
  title,
  children,
  className = '',
  iconSize = 3.5,
  variant = 'dark',
  inline = false,
  testId,
}) {
  const [open, setOpen] = useState(false);

  const colorClass =
    variant === 'light'
      ? 'text-white/50 hover:text-white/80'
      : 'text-slate-400 hover:text-[#4ABFCF]';

  const Tag = inline ? 'span' : 'button';

  return (
    <>
      <Tag
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center justify-center transition-colors cursor-pointer ${colorClass} ${className}`}
        data-testid={testId}
        title={`How is ${title} calculated?`}
        aria-label={`Explain ${title}`}
      >
        <Info style={{ width: `${iconSize * 4}px`, height: `${iconSize * 4}px` }} />
      </Tag>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid={testId ? `${testId}-dialog` : undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5 text-[#0F2040]">
              <div className="w-8 h-8 rounded-lg bg-[#4ABFCF]/10 flex items-center justify-center shrink-0">
                <Info className="w-4 h-4 text-[#4ABFCF]" />
              </div>
              {title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-600">{children}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * FormulaBlock — renders a formula in a styled mono box.
 */
export function FormulaBlock({ lines, className = '' }) {
  return (
    <div className={`bg-white rounded-md px-3 py-2 font-mono text-[11px] border border-slate-200 space-y-0.5 ${className}`}>
      {lines.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
}

/**
 * CalcStep — renders a single "label = value" row in a calculation breakdown.
 */
export function CalcStep({ label, value, highlight, bold }) {
  return (
    <div className={`flex justify-between items-baseline text-xs ${bold ? 'font-semibold border-t border-slate-200 pt-1.5 mt-1.5' : ''} ${highlight ? 'text-[#0F2040]' : 'text-slate-600'}`}>
      <span>{label}</span>
      <span className={`font-mono tabular-nums ${highlight ? 'text-[#4ABFCF] font-semibold' : ''}`}>{value}</span>
    </div>
  );
}

/**
 * MathSection — a labelled section inside a MathExplainer dialog.
 */
export function MathSection({ title, children }) {
  return (
    <div>
      <h4 className="font-semibold text-[#0F2040] mb-2 text-sm">{title}</h4>
      {children}
    </div>
  );
}
